import { FormEvent, useEffect, useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { Button, Card, Input } from '@bruno/ui';
import type { PublicCaptchaConfigDTO, TwoFactorChallengeResponse, UserDTO } from '@bruno/shared';
import { isTwoFactorChallenge } from '@bruno/shared';
import { useAuth } from '../auth/AuthContext';
import { useCapCaptcha } from '../auth/useCapCaptcha';
import { getCaptchaConfig, logout } from '../api/auth';
import { twoFactorLogin } from '../api/account';
import { CaptchaStatusBadge } from '../components/CaptchaStatusBadge';

export function LoginPage() {
  const { user, login, setUser } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [captchaCfg, setCaptchaCfg] = useState<PublicCaptchaConfigDTO | null>(null);
  const [challenge, setChallenge] = useState<TwoFactorChallengeResponse | null>(null);

  useEffect(() => {
    let cancelled = false;
    void getCaptchaConfig()
      .then((cfg) => {
        if (!cancelled) setCaptchaCfg(cfg);
      })
      .catch(() => {
        if (!cancelled) setCaptchaCfg({ enabled: false, instanceUrl: '', siteKey: '' });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const captcha = useCapCaptcha(captchaCfg ?? undefined);

  if (user?.role === 'admin') return <Navigate to="/" replace />;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const token = captchaCfg?.enabled ? await captcha.getToken() : undefined;
      if (captchaCfg?.enabled && !token) {
        throw new Error('Captcha verification failed. Please try again.');
      }
      const res = await login(email, password, token ?? undefined);
      if (isTwoFactorChallenge(res)) {
        if (captchaCfg?.enabled) captcha.reset();
        setChallenge(res);
        return;
      }
      navigate('/');
    } catch (err) {
      if (captchaCfg?.enabled) captcha.reset();
      setError(err instanceof Error ? err.message : 'Login failed');
    } finally {
      setLoading(false);
    }
  }

  if (challenge) {
    return (
      <TwoFactorChallengeForm
        challenge={challenge}
        captchaCfg={captchaCfg}
        onCancel={() => {
          setChallenge(null);
          setError(null);
        }}
        onSuccess={async (u) => {
          // Same gate as the password-only path: the admin app only
          // accepts the `admin` role; everyone else gets bounced.
          if (u.role !== 'admin') {
            await logout();
            setError('This account is not an administrator.');
            setChallenge(null);
            return;
          }
          setUser(u);
          navigate('/');
        }}
      />
    );
  }

  return (
    <div className="min-h-screen grid place-items-center bg-slate-900">
      <Card className="w-full max-w-md bg-white">
        <div className="mb-6 flex items-center gap-3">
          <img src="/bruno-logo.svg" alt="Bruno" className="h-10 w-10" />
          <div>
            <h1 className="text-xl font-semibold">Bruno Admin</h1>
            <p className="text-sm text-slate-500">Sign in to the operator console</p>
          </div>
        </div>
        <form onSubmit={onSubmit} className="flex flex-col gap-4">
          <Input
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoFocus
          />
          <Input
            label="Password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
          <div className="text-right -mt-2">
            <Link to="/forgot-password" className="text-sm text-indigo-600 hover:underline">
              Forgot password?
            </Link>
          </div>
          {error ? <div className="text-sm text-red-600">{error}</div> : null}
          <Button type="submit" loading={loading}>
            Sign in
          </Button>
          {captchaCfg?.enabled ? <CaptchaStatusBadge state={captcha.state} /> : null}
        </form>
      </Card>
    </div>
  );
}

function TwoFactorChallengeForm({
  challenge,
  captchaCfg,
  onCancel,
  onSuccess,
}: {
  challenge: TwoFactorChallengeResponse;
  captchaCfg: PublicCaptchaConfigDTO | null;
  onCancel: () => void;
  onSuccess: (user: UserDTO) => void;
}) {
  const [mode, setMode] = useState<'totp' | 'recovery'>('totp');
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Fresh Cap token for the challenge step — Cap tokens are
  // single-use, so the one burned at the password step won't do.
  const captcha = useCapCaptcha(captchaCfg ?? undefined);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const captchaToken = captchaCfg?.enabled ? await captcha.getToken() : undefined;
      if (captchaCfg?.enabled && !captchaToken) {
        throw new Error('Captcha verification failed. Please try again.');
      }
      const res = await twoFactorLogin({
        twoFactorToken: challenge.twoFactorToken,
        code: mode === 'totp' ? code.trim() : undefined,
        recoveryCode: mode === 'recovery' ? code.trim() : undefined,
        captchaToken: captchaToken ?? undefined,
      });
      onSuccess(res.user);
    } catch (err) {
      if (captchaCfg?.enabled) captcha.reset();
      setError(err instanceof Error ? err.message : 'Verification failed');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen grid place-items-center bg-slate-900">
      <Card className="w-full max-w-md bg-white">
        <h1 className="text-xl font-semibold">Two-factor authentication</h1>
        <p className="text-sm text-slate-500 mt-1">
          {mode === 'totp'
            ? 'Enter the 6-digit code from your authenticator app.'
            : 'Enter one of your one-time recovery codes.'}
        </p>
        <form onSubmit={submit} className="flex flex-col gap-4 mt-5">
          <Input
            label={mode === 'totp' ? 'Authenticator code' : 'Recovery code'}
            value={code}
            onChange={(e) => setCode(e.target.value)}
            inputMode={mode === 'totp' ? 'numeric' : 'text'}
            autoComplete="one-time-code"
            autoFocus
            required
          />
          {error ? <div className="text-sm text-red-600">{error}</div> : null}
          <Button type="submit" loading={submitting}>
            Verify
          </Button>
          {captchaCfg?.enabled ? <CaptchaStatusBadge state={captcha.state} /> : null}
          <button
            type="button"
            className="text-sm text-slate-600 hover:underline self-center"
            onClick={() => {
              setMode((m) => (m === 'totp' ? 'recovery' : 'totp'));
              setCode('');
              setError(null);
            }}
          >
            {mode === 'totp'
              ? "I don't have access to my authenticator — use a recovery code"
              : 'Use my authenticator app instead'}
          </button>
          <button
            type="button"
            className="text-xs text-slate-500 hover:underline self-center"
            onClick={onCancel}
          >
            Cancel and start over
          </button>
        </form>
      </Card>
    </div>
  );
}
